import type { ApiGnomeSnapshot } from "@bridge-applications/pb-types";
import type { GnomeCustomizationSlice } from "../../schemas/slices/GnomeCustomizationSliceSchema.ts";
import type { GnomeSnapshotOwner } from "./gnomeSnapshotReader.ts";
import { equippedItemsFromSnapshot } from "./gnomeSnapshotAppearance.ts";
import {
    isCurrentGnomeSnapshotVersion,
    resolveGnomeCustomizationSync,
} from "./gnomeCustomizationSync.ts";

export function ownsGnomeSnapshot(
    state: Pick<
        GnomeCustomizationSlice,
        "snapshotUserId" | "snapshotGeneration"
    >,
    owner: GnomeSnapshotOwner,
): boolean {
    return (
        state.snapshotUserId === owner.userId &&
        state.snapshotGeneration === owner.generation
    );
}

export function acceptGnomeSnapshot(
    previous: GnomeCustomizationSlice,
    incoming: ApiGnomeSnapshot,
    owner: GnomeSnapshotOwner,
    source: "background" | "foreground",
    completingMutation: boolean,
    receivedAt: number,
): GnomeCustomizationSlice {
    if (!ownsGnomeSnapshot(previous, owner)) return previous;
    if (source === "background" && previous.pendingMutationCount > 0)
        return { ...previous, backgroundReadStatus: "ready" };
    const current = isCurrentGnomeSnapshotVersion(
        incoming.character.version,
        previous.snapshot?.character.version,
    );
    const snapshot = current ? incoming : (previous.snapshot ?? incoming);
    return {
        ...previous,
        ...(source === "foreground"
            ? {
                  ...resolveGnomeCustomizationSync(
                      previous.pendingMutationCount,
                      "ready",
                      completingMutation,
                  ),
                  syncError: null,
              }
            : { backgroundReadStatus: "ready" as const }),
        snapshot,
        snapshotFetchedAt: current ? receivedAt : previous.snapshotFetchedAt,
        displayName: snapshot.character.name,
        facialExpression: snapshot.character.baseCustomization.facialExpression,
        skinColorId: snapshot.character.baseCustomization.skinColorId,
        equippedItemIds: equippedItemsFromSnapshot(snapshot),
    };
}
